import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Avatar,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Input,
  List,
  Modal,
  Pagination,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Divider,
  Tabs,
  Tag,
  Typography,
  Upload,
  message
} from 'antd'
import {
  CarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  LogoutOutlined,
  SearchOutlined,
  UserOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { useVehicleStore } from '../stores/vehicleStore'
import { simpleDirectCloudApi } from '../services/directCloudApi.simple'
import { app, ensureLogin } from '../services/tcbClient'
import type { Vehicle, VehicleAdjustment } from '../types'
import {
  allocatePaymentByPeriod as allocatePaymentByPeriodUtil,
  formatPeriodLabel,
  getVehicleDueDate,
  isLeaseStarted,
  getOutstanding as getOutstandingUtil,
  getPeriodDeficits as getPeriodDeficitsUtil,
  type PreviewItem,
  type ReceiptRecord
} from './vehicleManagerUtils'
import { ExpiringVehiclesModal, PreviewModal, RentModal } from './VehicleManagerModals'

const { Title, Text } = Typography
const { Option } = Select

const VehicleManagerPage: React.FC = () => {
  const navigate = useNavigate()
  const { user, logout, checkPermission } = useAuthStore()
  const { vehicles, statistics, fetchVehicles, fetchStatistics, updateVehicle } = useVehicleStore()

  const [activeTab, setActiveTab] = useState<'rent' | 'pending' | 'paid' | 'rentAdjust'>('rent')
  const [rentModalVisible, setRentModalVisible] = useState(false)
  const [expiringModalVisible, setExpiringModalVisible] = useState(false)
  const [loading, setLoading] = useState(true)
  const [searchKeyword, setSearchKeyword] = useState('')
  const [areaFilter, setAreaFilter] = useState<'all' | 'A' | 'B' | 'C' | 'D'>('all')
  const [showAllExpiring, setShowAllExpiring] = useState(false)
  const [expiringPage, setExpiringPage] = useState(1)
  const [expiringPageSize, setExpiringPageSize] = useState(8)
  const [activeExpiringVehicleId, setActiveExpiringVehicleId] = useState<string>('')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [fileList, setFileList] = useState<any[]>([])
  const [previewVisible, setPreviewVisible] = useState(false)
  const [previewItems, setPreviewItems] = useState<PreviewItem[]>([])
  const [pendingValues, setPendingValues] = useState<any>(null)
  const [pendingReceipts, setPendingReceipts] = useState<ReceiptRecord[]>([])
  const [paidReceipts, setPaidReceipts] = useState<ReceiptRecord[]>([])
  const [allReceipts, setAllReceipts] = useState<ReceiptRecord[]>([])
  const [adjustments, setAdjustments] = useState<VehicleAdjustment[]>([])
  const [leaseModalVisible, setLeaseModalVisible] = useState(false)
  const [leaseForm] = Form.useForm()
  const leaseType = Form.useWatch('type', leaseForm)
  const leaseUnitName = Form.useWatch('unitName', leaseForm)
  const leaseVehicleId = Form.useWatch('vehicleId', leaseForm)
  const leaseMakerId = Form.useWatch('makerId', leaseForm)
  const [rentAdjustModalVisible, setRentAdjustModalVisible] = useState(false)
  const [rentAdjustForm] = Form.useForm()
  const rentAdjustVehicleId = Form.useWatch('vehicleId', rentAdjustForm)
  const rentAdjustMakerId = Form.useWatch('makerId', rentAdjustForm)
  const [rentAdjustFileList, setRentAdjustFileList] = useState<any[]>([])
  const [rentAdjustRequests, setRentAdjustRequests] = useState<any[]>([])
  const [signedLeaseForms, setSignedLeaseForms] = useState<any[]>([])
  const [leaseMakerSealPreviewUrl, setLeaseMakerSealPreviewUrl] = useState('')
  const [rentAdjustMakerSealPreviewUrl, setRentAdjustMakerSealPreviewUrl] = useState('')
  const [selectedVehicles, setSelectedVehicles] = useState<Vehicle[]>([])
  const [mergeByCompany, setMergeByCompany] = useState(false)
  const [form] = Form.useForm()
  const [makers, setMakers] = useState<any[]>([])
  const rentDetailsRef = useRef<HTMLDivElement | null>(null)
  const canManageVehicles = checkPermission('manage_vehicles')
  const canViewBoss = checkPermission('view_boss')
  useEffect(() => {
    loadData()
  }, [])

  useEffect(() => {
    if (!leaseModalVisible) return
    const currentMakerId = leaseForm.getFieldValue('makerId')
    if (currentMakerId) return
    const match = makers.find(m =>
      [m._id, m.id, m.username, m.nickName].some(val => String(val || '') === String(user?.id || user?.nickName || ''))
    )
    if (match) {
      leaseForm.setFieldsValue({ makerId: String(match._id || match.id || match.username || match.nickName) })
    }
  }, [leaseModalVisible, leaseForm, user, makers])

  useEffect(() => {
    if (!rentAdjustModalVisible) return
    const currentMakerId = rentAdjustForm.getFieldValue('makerId')
    if (currentMakerId) return
    const match = makers.find(m =>
      [m._id, m.id, m.username, m.nickName].some(val => String(val || '') === String(user?.id || user?.nickName || ''))
    )
    if (match) {
      rentAdjustForm.setFieldsValue({ makerId: String(match._id || match.id || match.username || match.nickName) })
    }
  }, [rentAdjustModalVisible, rentAdjustForm, user, makers])

  useEffect(() => {
    const isReturnMode = !leaseType || leaseType === 'return'
    if (!isReturnMode) return
    const vehicle = vehicles.find(v => String(v._id || v.id || v.plate) === String(leaseVehicleId))
    if (!vehicle) return
    const currentUnitName = String(leaseForm.getFieldValue('unitName') || '')
    if (currentUnitName === String(vehicle.company || '')) return
    leaseForm.setFieldsValue({ unitName: vehicle.company || '' })
  }, [leaseType, leaseVehicleId, vehicles, leaseForm])

  const openLeaseModal = () => {
    leaseForm.resetFields()
    const match = makers.find(m =>
      [m._id, m.id, m.username, m.nickName].some(val => String(val || '') === String(user?.id || user?.nickName || ''))
    )
    const defaultMakerId = match ? String(match._id || match.id || match.username || match.nickName) : undefined
    leaseForm.setFieldsValue({
      type: 'return',
      makerId: defaultMakerId
    })
    setLeaseModalVisible(true)
  }

  const loadData = async () => {
    try {
      setLoading(true)
      await Promise.all([fetchVehicles(), fetchStatistics()])
      const [pendingResult, paidResult, allResult, adjustmentsResult, makersResult, rentAdjustResult] = await Promise.all([
        simpleDirectCloudApi.getReceipts('submitted'),
        simpleDirectCloudApi.getReceipts('accounted'),
        simpleDirectCloudApi.getReceipts(),
        simpleDirectCloudApi.getVehicleAdjustments(),
        simpleDirectCloudApi.getAvailableUsers(),
        simpleDirectCloudApi.getRentAdjustRequests()
      ])
      const leaseResult = await simpleDirectCloudApi.getLeaseForms('signed')
      setPendingReceipts(pendingResult.success && pendingResult.data ? pendingResult.data : [])
      setPaidReceipts(paidResult.success && paidResult.data ? paidResult.data : [])
      setAllReceipts(allResult.success && allResult.data ? allResult.data : [])
      const adjustmentsRaw = adjustmentsResult.success && adjustmentsResult.data ? adjustmentsResult.data : []
      setAdjustments(adjustmentsRaw.filter((adj: any) => adj.type !== 'rent_addition'))
      const makersRaw = makersResult.success && makersResult.data ? makersResult.data : []
      setMakers(makersRaw.filter((m: any) => m.isActive !== false))
      setRentAdjustRequests(rentAdjustResult.success && rentAdjustResult.data ? rentAdjustResult.data : [])
      setSignedLeaseForms(leaseResult.success && leaseResult.data ? leaseResult.data : [])
    } catch (err) {
      console.error(err)
      message.error('加载数据失败')
    } finally {
      setLoading(false)
    }
  }
  const normalizeArea = (value?: string) => {
    const raw = String(value || '').trim()
    if (!raw) return ''
    const match = raw.match(/^[A-D]/i)
    return match ? match[0].toUpperCase() : raw.toUpperCase()
  }

  const filteredRentedVehicles = useMemo(() => {
    const keyword = searchKeyword.trim().toLowerCase()
    return vehicles
      .filter(v => v.status === 'rented')
      .filter(v => (areaFilter === 'all' ? true : normalizeArea(v.area) === areaFilter))
      .filter(v => {
        if (!keyword) return true
        const plate = String(v.plate || '').toLowerCase()
        const company = String(v.company || '').toLowerCase()
        const contact1 = String(v.contactPerson || '').toLowerCase()
        const contact2 = String((v as any).contactPerson2 || '').toLowerCase()
        const phone = String(v.phone || '')
        const phone2 = String((v as any).phone2 || '')
        return (
          plate.includes(keyword) ||
          company.includes(keyword) ||
          contact1.includes(keyword) ||
          contact2.includes(keyword) ||
          phone.includes(keyword) ||
          phone2.includes(keyword)
        )
      })
  }, [vehicles, searchKeyword, areaFilter])

  const paginatedRentedVehicles = useMemo(() => {
    const start = (currentPage - 1) * pageSize
    return filteredRentedVehicles.slice(start, start + pageSize)
  }, [filteredRentedVehicles, currentPage, pageSize])
  const handleLogout = () => {
    Modal.confirm({
      title: '确认退出？',
      onOk: () => {
        logout()
        navigate('/login')
      }
    })
  }

  const handleSearch = (value: string) => {
    setSearchKeyword(value)
    setCurrentPage(1)
  }

  const jumpToVehicleDetails = (vehicle?: Vehicle) => {
    setActiveTab('rent')
    setAreaFilter('all')
    if (vehicle) {
      const keyword = String(vehicle.plate || '').trim()
      setSearchKeyword(keyword)
      setCurrentPage(1)
    } else {
      setSearchKeyword('')
      setCurrentPage(1)
    }
    window.setTimeout(() => {
      rentDetailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 80)
  }

  const normalizeId = (value: any) => String(value ?? '')
  const { latestChargeRangeEndByVehicleId, latestChargeRangeEndByPlate } = useMemo(() => {
    const byVehicleId = new Map<string, string>()
    const byPlate = new Map<string, string>()
    for (const receipt of allReceipts) {
      const items = Array.isArray((receipt as any)?.items) ? (receipt as any).items : []
      for (const item of items) {
        const vehicleId = normalizeId(item?.vehicleId || item?._id || item?.id)
        const plate = String(item?.plate || '').trim()
        const end = String(item?.chargeRangeEnd || '')
        if (!end || !dayjs(end).isValid()) continue
        if (vehicleId) {
          const prev = byVehicleId.get(vehicleId)
          if (!prev || dayjs(end).isAfter(dayjs(prev), 'day')) {
            byVehicleId.set(vehicleId, end)
          }
        }
        if (plate) {
          const prevByPlate = byPlate.get(plate)
          if (!prevByPlate || dayjs(end).isAfter(dayjs(prevByPlate), 'day')) {
            byPlate.set(plate, end)
          }
        }
      }
    }
    return {
      latestChargeRangeEndByVehicleId: byVehicleId,
      latestChargeRangeEndByPlate: byPlate
    }
  }, [allReceipts])
  const resolveMaker = (value?: string) =>
    makers.find(m =>
      [m._id, m.id, m.username, m.nickName].some(val => String(val || '') === String(value || ''))
    )
  const leaseMakerSeal = resolveMaker(leaseMakerId)?.makerSealUrl
  const rentAdjustMakerSeal = resolveMaker(rentAdjustMakerId)?.makerSealUrl

  const resolveSealPreviewUrl = async (fileId?: string) => {
    if (!fileId) return ''
    if (!String(fileId).startsWith('cloud://')) return String(fileId)
    await ensureLogin()
    const storage = (app as any).storage?.()
    const fileList = [{ fileID: fileId, maxAge: 24 * 60 * 60 }]
    const res: any = storage?.getTempFileURL
      ? await storage.getTempFileURL({ fileList })
      : await (app as any).getTempFileURL({ fileList })
    return (
      res?.fileList?.[0]?.tempFileURL ||
      res?.fileList?.[0]?.tempFileUrl ||
      res?.fileList?.[0]?.download_url ||
      ''
    )
  }

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const url = await resolveSealPreviewUrl(leaseMakerSeal)
        if (!cancelled) setLeaseMakerSealPreviewUrl(url)
      } catch {
        if (!cancelled) setLeaseMakerSealPreviewUrl('')
      }
    }
    load()
    return () => { cancelled = true }
  }, [leaseMakerSeal])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const url = await resolveSealPreviewUrl(rentAdjustMakerSeal)
        if (!cancelled) setRentAdjustMakerSealPreviewUrl(url)
      } catch {
        if (!cancelled) setRentAdjustMakerSealPreviewUrl('')
      }
    }
    load()
    return () => { cancelled = true }
  }, [rentAdjustMakerSeal])

  const getOutstanding = (vehicle: Vehicle) => getOutstandingUtil(vehicle, allReceipts, adjustments)
  const getPeriodDeficits = (vehicle: Vehicle) => getPeriodDeficitsUtil(vehicle, allReceipts)
  const getPeriodLabel = (value?: string, months?: number) => formatPeriodLabel(value, months)
  const allocatePaymentByPeriod = (vehicle: Vehicle, payAmount: number, customMonths?: number) =>
    allocatePaymentByPeriodUtil(vehicle, payAmount, allReceipts, customMonths)
  const getReceiptDeadlineForVehicle = (vehicle?: Vehicle) => {
    if (!vehicle) return ''
    return (
      latestChargeRangeEndByVehicleId.get(normalizeId(vehicle._id || vehicle.id || vehicle.plate)) ||
      latestChargeRangeEndByPlate.get(String(vehicle.plate || '').trim()) ||
      String(vehicle.startDate || '')
    )
  }
  const buildDefaultChargeRange = (vehicle?: Vehicle) => {
    if (!vehicle) return undefined
    const startRaw = getReceiptDeadlineForVehicle(vehicle)
    if (!startRaw || !dayjs(startRaw).isValid()) return undefined
    const start = dayjs(startRaw)
    const dueRaw = getVehicleDueDate(vehicle)
    const due = dueRaw && dayjs(dueRaw).isValid() ? dayjs(dueRaw) : start
    const end = due.isBefore(start, 'day') ? start : due
    return [start, end]
  }
  const resolveVehicleByAnyId = (value: any) => {
    const target = String(value ?? '').trim().toUpperCase()
    if (!target) return undefined
    return vehicles.find(v => {
      const keys = [v._id, v.id, v.plate, (v as any).forklift_no].map(k => String(k ?? '').trim().toUpperCase())
      return keys.includes(target)
    })
  }
  const handleVehicleSelectionChange = (ids: string[]) => {
    const normalizedIds = ids.map(normalizeId).filter(Boolean)
    console.log('[VehicleManager] selection ids', ids, 'normalized', normalizedIds)
    const selected = vehicles.filter(v => normalizedIds.includes(normalizeId(v._id || v.id || v.plate)))
    console.log('[VehicleManager] selected vehicles', selected.map(v => ({
      id: v._id || v.id || v.plate,
      plate: v.plate
    })))
    setSelectedVehicles(selected)

    const existingItems: any[] = form.getFieldValue('items') || []
    const nextItems = normalizedIds.map((id: string) => {
      const v = selected.find(item => normalizeId(item._id || item.id || item.plate) === id)
      const keep = existingItems.find(it => normalizeId(it.vehicleId) === id)
      if (!v && !keep) {
        console.warn('[VehicleManager] no match for id', id)
      }
      if (keep) {
        return {
          ...keep,
          chargeRange: keep.chargeRange || buildDefaultChargeRange(v)
        }
      }
      return {
        vehicleId: id,
        amount: v?.rentAmount || 0,
        dueDate: v?.dueDate ? dayjs(v.dueDate) : null,
        startDate: v?.startDate ? dayjs(v.startDate) : null,
        chargeRange: buildDefaultChargeRange(v),
        rentPeriod: 'yearly',
        rentPeriodMonths: undefined
      }
    })
    console.log('[VehicleManager] nextItems', nextItems)
    form.setFieldsValue({ vehicleIds: normalizedIds, items: nextItems.filter(it => normalizeId(it.vehicleId)) })
  }

  const handleOpenRentForm = (vehicle?: Vehicle | null) => {
    const target = vehicle || null
    if (target?.returnSigned) {
      message.error('该车辆已退租，不能再收款')
      return
    }
    form.resetFields()
    const makerMatch = makers.find(m =>
      [m._id, m.id, m.username, m.nickName].some(val => String(val || '') === String(user?.id || user?.nickName || ''))
    )
    const defaultMakerId = makerMatch
      ? String(makerMatch._id || makerMatch.id || makerMatch.username || makerMatch.nickName)
      : undefined
    const vehicleId = target?._id || target?.id || target?.plate
      ? normalizeId(target?._id || target?.id || target?.plate)
      : ''
    const ids = vehicleId ? [vehicleId] : []
    const initialItems = vehicleId
      ? [
          {
            vehicleId,
            amount: target?.rentAmount || 0,
            dueDate: target?.dueDate ? dayjs(target.dueDate) : null,
            startDate: target?.startDate ? dayjs(target.startDate) : null,
            chargeRange: buildDefaultChargeRange(target as Vehicle),
            rentPeriod: 'yearly',
            rentPeriodMonths: undefined
          }
        ]
      : []
    setSelectedVehicles(ids.length ? [target as Vehicle] : [])
    form.setFieldsValue({
      vehicleIds: ids,
      items: initialItems,
      notes: '',
      totalAmount: undefined,
      mergeByCompany: false,
      makerId: defaultMakerId
    })
    setMergeByCompany(false)
    setFileList([])
    setRentModalVisible(true)
    if (ids.length) {
      window.setTimeout(() => handleVehicleSelectionChange(ids), 0)
    }
  }

  useEffect(() => {
    if (!rentModalVisible) return
    const ids: string[] = (form.getFieldValue('vehicleIds') || []).map((id: any) => normalizeId(id)).filter(Boolean)
    if (ids.length) {
      const itemsNow: any[] = form.getFieldValue('items') || []
      const hasMissingVehicleId = itemsNow.some((item: any) => !normalizeId(item?.vehicleId))
      const hasMissingRange = itemsNow.some((item: any) => !(Array.isArray(item?.chargeRange) && item?.chargeRange[0] && item?.chargeRange[1]))
      if (hasMissingVehicleId || hasMissingRange || itemsNow.length !== ids.length) {
        handleVehicleSelectionChange(ids)
        return
      }
    }
    const items: any[] = form.getFieldValue('items') || []
    if (!items.length) return
    let changed = false
    const patched = items.map((item: any) => {
      const hasRange = Array.isArray(item?.chargeRange) && item.chargeRange[0] && item.chargeRange[1]
      if (hasRange) return item
      const v = resolveVehicleByAnyId(item?.vehicleId)
      const range = buildDefaultChargeRange(v)
      if (!range) return item
      changed = true
      return { ...item, chargeRange: range }
    })
    if (changed) {
      form.setFieldsValue({ items: patched })
    }
  }, [rentModalVisible, form, vehicles, allReceipts])

  const handleSubmitLeaseForm = async () => {
    try {
      const values = await leaseForm.validateFields()
      const vehicleId = values.vehicleId
      const vehicle = vehicles.find(v => String(v._id || v.id || v.plate) === String(vehicleId))
      if (!vehicle) {
        message.error('车辆不存在')
        return
      }
      const type = values.type
      const unitName = values.unitName
      const notes = values.notes || ''
      const makerProfile = resolveMaker(values.makerId)
      const makerName = makerProfile?.nickName || user?.nickName || user?.id
      const res = await simpleDirectCloudApi.addLeaseForm({
        type,
        vehicleId: vehicle._id || vehicle.id || vehicle.plate,
        plate: vehicle.plate,
        model: vehicle.type,
        configuration: vehicle.configuration || vehicle.notes || '',
        company: vehicle.company,
        unitName,
        notes,
        createdBy: makerName,
        makerId: makerProfile?._id || makerProfile?.id || makerProfile?.username,
        makerSealUrl: makerProfile?.makerSealUrl
      })
      if (res.success) {
        message.success('已提交，等待出纳核验')
        setLeaseModalVisible(false)
        leaseForm.resetFields()
      } else {
        message.error(res.error || '提交失败')
      }
    } catch (err: any) {
      message.error(err?.message || '提交失败')
    }
  }

  const handleSubmitRentAdjustRequest = async () => {
    try {
      const values = await rentAdjustForm.validateFields()
      const vehicleId = values.vehicleId
      const vehicle = vehicles.find(v => String(v._id || v.id || v.plate) === String(vehicleId))
      if (!vehicle) {
        message.error('车辆不存在')
        return
      }
      const makerProfile = resolveMaker(values.makerId)
      const makerName = makerProfile?.nickName || user?.nickName || user?.id
      const attachmentUrls: string[] = []
      for (const file of rentAdjustFileList) {
        if (!file.originFileObj) continue
        const cloudPath = `rent_adjust_requests/${vehicle.plate || vehicleId}-${Date.now()}-${file.name}`
        const uploadRes: any = await app.uploadFile({
          cloudPath,
          filePath: file.originFileObj
        } as any)
        if (uploadRes?.fileID) attachmentUrls.push(uploadRes.fileID)
      }
      const res = await simpleDirectCloudApi.addRentAdjustRequest({
        vehicleId: vehicle._id || vehicle.id || vehicle.plate,
        plate: vehicle.plate,
        model: vehicle.type,
        configuration: vehicle.configuration || vehicle.notes || '',
        company: vehicle.company,
        currentRent: Number(vehicle.rentAmount || 0),
        notes: values.notes || '',
        attachmentUrls,
        status: 'filed',
        createdBy: makerName,
        makerId: makerProfile?._id || makerProfile?.id || makerProfile?.username,
        makerSealUrl: makerProfile?.makerSealUrl
      })
      if (res.success) {
        message.success('已提交并归档')
        setRentAdjustModalVisible(false)
        rentAdjustForm.resetFields()
        setRentAdjustFileList([])
        loadData()
      } else {
        message.error(res.error || '提交失败')
      }
    } catch (err: any) {
      message.error(err?.message || '提交失败')
    }
  }
  const handlePreviewRentForm = async () => {
    try {
      const values = await form.validateFields()
      const idsFromForm: string[] = values.vehicleIds || []
      const fallbackIds = selectedVehicles.map(v => v._id || v.id || v.plate).filter(Boolean) as string[]
      const itemIds = (values.items || []).map((item: any) => item?.vehicleId).filter(Boolean)
      const ids: string[] = idsFromForm.length ? idsFromForm : (fallbackIds.length ? fallbackIds : itemIds)
      let items = values.items || []
      if (!ids.length) {
        message.error('请选择车辆')
        return
      }
      if (values.mergeByCompany) {
        const total = Number(values.totalAmount || 0)
        if (!total || total <= 0) {
          message.error('请输入正确的金额')
          return
        }
      }
      if (!items.length) {
        message.error('请选择车辆')
        return
      }
      const preview: PreviewItem[] = items.map((item: any) => {
        const vehicle =
          selectedVehicles.find(v => normalizeId(v._id || v.id || v.plate) === normalizeId(item.vehicleId)) ||
          vehicles.find(v => normalizeId(v._id || v.id || v.plate) === normalizeId(item.vehicleId))!
        const chargeRangeStart = item.chargeRange?.[0]?.format?.('YYYY-MM-DD')
        const chargeRangeEnd = item.chargeRange?.[1]?.format?.('YYYY-MM-DD')
        return {
          vehicle,
          amount: Number(item.amount || 0),
          actualAmount: Number(item.actualAmount || 0),
          dueDate: item.dueDate ? item.dueDate.format('YYYY-MM-DD') : getVehicleDueDate(vehicle),
          startDate: item.startDate ? item.startDate.format('YYYY-MM-DD') : vehicle?.startDate,
          rentPeriod: 'yearly',
          rentPeriodMonths: undefined,
          chargeRangeStart,
          chargeRangeEnd,
          outstanding: getOutstanding(vehicle)
        }
      })
      setPendingValues({ ...values, vehicleIds: ids, mergeByCompany: values.mergeByCompany ?? mergeByCompany, items })
      setPreviewItems(preview)
      setPreviewVisible(true)
    } catch {
      message.error('预览失败')
    }
  }

  const uploadEvidence = async (vehicleTag: string) => {
    const evidenceUrls: string[] = []
    for (const file of fileList) {
      if (!file.originFileObj) continue
      const cloudPath = `receipts/${vehicleTag}-${Date.now()}-${file.name}`
      const res: any = await app.uploadFile({
        cloudPath,
        filePath: file.originFileObj
      } as any)
      if (res?.fileID) {
        evidenceUrls.push(res.fileID)
      }
    }
    return evidenceUrls
  }

  const openCloudFile = async (fileId: string) => {
    try {
      if (!fileId) return
      const url = await resolveCloudFileUrl(fileId)
      if (!url) {
        message.error('文件链接无效')
        return
      }
      window.open(url, '_blank')
    } catch (err) {
      console.error(err)
      message.error('打开文件失败')
    }
  }

  const resolveCloudFileUrl = async (fileId?: string) => {
    if (!fileId) return ''
    if (!String(fileId).startsWith('cloud://')) return String(fileId)
    await ensureLogin()
    const storage = (app as any).storage?.()
    const fileList = [{ fileID: fileId, maxAge: 24 * 60 * 60 }]
    const res: any = storage?.getTempFileURL
      ? await storage.getTempFileURL({ fileList })
      : await (app as any).getTempFileURL({ fileList })
    return (
      res?.fileList?.[0]?.tempFileURL ||
      res?.fileList?.[0]?.tempFileUrl ||
      res?.fileList?.[0]?.download_url ||
      ''
    )
  }

  const getReceiptItems = (receipt: any) => {
    if (Array.isArray(receipt?.items)) return receipt.items
    if (receipt?.items && typeof receipt.items === 'object') return Object.values(receipt.items)
    return []
  }

  const signedReceiptDocs = useMemo(() => {
    const doneStatuses = ['cashier_done', 'accounted', 'completed']
    return allReceipts
      .filter((r: any) => doneStatuses.includes(String(r.status || '')))
      .filter((r: any) => {
        if (r.cashierSignatureUrl) return true
        const items = getReceiptItems(r)
        return items.some((it: any) => it.cashierSignatureUrl)
      })
      .sort((a: any, b: any) => dayjs(b.verifiedAt || b.updatedAt || b.createdAt).valueOf() - dayjs(a.verifiedAt || a.updatedAt || a.createdAt).valueOf())
  }, [allReceipts])

  const printHtml = (title: string, body: string) => {
    const html = `
      <html>
        <head>
          <title>${title}</title>
          <style>
            body { margin: 24px; font-family: "SimSun","STSong","Noto Serif SC",serif; color: #222; }
            table { width: 100%; border-collapse: collapse; margin-top: 12px; }
            th, td { border: 1px solid #333; padding: 6px 8px; text-align: left; vertical-align: top; }
            .right { text-align: right; }
            .row { display: flex; justify-content: space-between; margin-top: 6px; }
            img { max-width: 220px; max-height: 120px; border: 1px solid #ddd; border-radius: 4px; }
          </style>
        </head>
        <body>${body}</body>
      </html>
    `

    const iframe = document.createElement('iframe')
    iframe.style.position = 'fixed'
    iframe.style.right = '0'
    iframe.style.bottom = '0'
    iframe.style.width = '0'
    iframe.style.height = '0'
    iframe.style.border = '0'
    iframe.setAttribute('aria-hidden', 'true')
    document.body.appendChild(iframe)

    const cleanup = () => {
      setTimeout(() => {
        try { document.body.removeChild(iframe) } catch {}
      }, 300)
    }

    iframe.onload = () => {
      try {
        const frameWindow = iframe.contentWindow
        if (!frameWindow) {
          cleanup()
          return
        }
        frameWindow.focus()
        frameWindow.print()
      } finally {
        cleanup()
      }
    }

    const doc = iframe.contentDocument || iframe.contentWindow?.document
    if (!doc) {
      cleanup()
      return
    }
    doc.open()
    doc.write(html)
    doc.close()
  }

  const handlePrintReceipt = async (receipt: any) => {
    try {
      const items = getReceiptItems(receipt)
      const company = receipt.company || receipt.customerName || '-'
      const receiptNo = receipt._id || receipt.id || '-'
      const date = dayjs(receipt.verifiedAt || receipt.createdAt).isValid()
        ? dayjs(receipt.verifiedAt || receipt.createdAt).format('YYYY-MM-DD')
        : '-'
      const makerName = receipt.createdBy || '-'
      const cashierName = receipt.cashierName || '-'
      const total = items.reduce((sum: number, it: any) => sum + Number(it.receivedAmount ?? it.expectedAmount ?? it.amount ?? 0), 0)
      const signatureUrl = await resolveCloudFileUrl(receipt.cashierSignatureUrl || items.find((it: any) => it.cashierSignatureUrl)?.cashierSignatureUrl)
      const makerSealUrl = await resolveCloudFileUrl(receipt.makerSealUrl)
      const rows = items.map((it: any) => {
        const rangeStart = it.chargeRangeStart || it.periodStart || it.startDate || '-'
        const rangeEnd = it.chargeRangeEnd || it.periodEnd || it.dueDate || '-'
        const amount = Number(it.receivedAmount ?? it.expectedAmount ?? it.amount ?? 0)
        return `
          <tr>
            <td></td>
            <td>车辆租金（${it.plate || receipt.plate || '-'}）</td>
            <td>${rangeStart} ~ ${rangeEnd}</td>
            <td>台</td>
            <td>1</td>
            <td class="right">${amount.toFixed(2)}</td>
            <td class="right">${amount.toFixed(2)}</td>
          </tr>
        `
      }).join('')
      printHtml('收款收据', `
        <div style="text-align:center;font-size:28px;font-weight:700;letter-spacing:8px;">收款收据</div>
        <div class="row"><div>客户名称：${company}</div><div>No：${receiptNo}</div></div>
        <div class="row"><div>日期：${date}</div><div>制单人：${makerName}</div></div>
        <table>
          <thead>
            <tr>
              <th>货号</th><th>品名及规格</th><th>收款区间</th><th>单位</th><th>数量</th><th>单价</th><th>金额</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
        <div class="row"><div>合计人民币：${total.toFixed(2)}</div><div>￥${total.toFixed(2)}</div></div>
        <div class="row"><div>制单人：${makerName}</div><div>出纳：${cashierName}</div></div>
        ${makerSealUrl ? `<div style="margin-top:8px;text-align:right;"><img src="${makerSealUrl}" /></div>` : ''}
        ${signatureUrl ? `<div style="margin-top:8px;text-align:right;"><img src="${signatureUrl}" /></div>` : ''}
        <div style="margin-top:8px;">备注：${receipt.cashierNotes || receipt.notes || '-'}</div>
      `)
    } catch (err) {
      console.error(err)
      message.error('打印收据失败')
    }
  }

  const handlePrintLeaseForm = async (doc: any) => {
    try {
      const signatureUrl = await resolveCloudFileUrl(doc.cashierSignatureUrl)
      const makerSealUrl = await resolveCloudFileUrl(doc.makerSealUrl)
      const title = doc.type === 'return' ? '退租单' : '出租单'
      const unitLabel = doc.type === 'relet' ? '出租单位' : '退租单位'
      printHtml(title, `
        <div style="text-align:center;font-size:28px;font-weight:700;letter-spacing:8px;">${title}</div>
        <div class="row"><div>${unitLabel}：${doc.unitName || '-'}</div><div>No：${doc._id || doc.id || '-'}</div></div>
        <div class="row"><div>车牌：${doc.plate || '-'}</div><div>日期：${doc.createdAt ? dayjs(doc.createdAt).format('YYYY-MM-DD') : '-'}</div></div>
        <table>
          <thead>
            <tr>
              <th>货号</th><th>品名及规格</th><th>${unitLabel}</th><th>单位</th><th>数量</th><th>单价</th><th>金额</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td></td>
              <td>车辆租赁（${doc.plate || '-'}）${doc.model ? ` - ${doc.model}` : ''}</td>
              <td>${doc.unitName || '-'}</td>
              <td>台</td>
              <td>1</td>
              <td class="right">-</td>
              <td class="right">-</td>
            </tr>
          </tbody>
        </table>
        <div style="margin-top:8px;">配置：${doc.configuration || '-'}</div>
        <div class="row"><div>备注：${doc.notes || '-'}</div><div>状态：${doc.status === 'signed' ? '已签名' : '待签名'}</div></div>
        <div class="row"><div>制单人：${doc.createdBy || '-'}</div><div>出纳：${doc.cashierName || '-'}</div></div>
        ${makerSealUrl ? `<div style="margin-top:8px;text-align:right;"><img src="${makerSealUrl}" /></div>` : ''}
        ${signatureUrl ? `<div style="margin-top:8px;text-align:right;"><img src="${signatureUrl}" /></div>` : ''}
      `)
    } catch (err) {
      console.error(err)
      message.error('打印单据失败')
    }
  }
  const handleConfirmSubmit = async () => {
    if (!pendingValues) {
      setPreviewVisible(false)
      return
    }
    const { items: pendingItems = [], notes = '', mergeByCompany: mergeFlagRaw, totalAmount, vehicleIds: pendingIds, makerId } = pendingValues
    const mergeFlag = mergeFlagRaw ?? mergeByCompany
    let items = pendingItems
    const totalActual = items.reduce((sum: number, item: any) => sum + Number(item.actualAmount || 0), 0)
    const makerProfile = resolveMaker(makerId)
    const makerName = makerProfile?.nickName || user?.nickName || user?.id
    const makerIdValue = makerProfile?._id || makerProfile?.id || makerProfile?.username
    const makerSealUrl = makerProfile?.makerSealUrl
    const targetVehicles = (pendingIds && pendingIds.length ? pendingIds : selectedVehicles.map(v => v._id || v.id || v.plate)).map((id: string) =>
      vehicles.find(v => (v._id || v.id || v.plate) === id)
    ).filter(Boolean) as Vehicle[]

    try {
      if (mergeFlag) {
        const total = totalAmount ? Number(totalAmount) : items.reduce((s: number, it: any) => s + Number(it.amount || 0), 0)
        const itemsForSplit = items
        const allocatedItems: any[] = itemsForSplit.map((item: any) => {
          const vehicle =
            targetVehicles.find(v => (v._id || v.id || v.plate) === item.vehicleId) ||
            vehicles.find(v => (v._id || v.id || v.plate) === item.vehicleId)
          const startDate = item.startDate ? dayjs(item.startDate).format('YYYY-MM-DD') : vehicle?.startDate
          const dueDate = item.dueDate ? dayjs(item.dueDate).format('YYYY-MM-DD') : vehicle?.dueDate
          const rangeStart = item.chargeRange?.[0] ? dayjs(item.chargeRange[0]).format('YYYY-MM-DD') : undefined
          const rangeEnd = item.chargeRange?.[1] ? dayjs(item.chargeRange[1]).format('YYYY-MM-DD') : undefined
          return {
            vehicleId: vehicle?._id || vehicle?.id || item.vehicleId,
            leaseId: (vehicle as any)?.currentLeaseId,
            plate: vehicle?.plate || item.plate,
            expectedAmount: Number(item.amount || 0),
            receivedAmount: Number(item.amount || 0),
            actualAmount: Number(item.actualAmount || 0),
            periodKey: 'merge',
            periodStart: startDate || '',
            periodEnd: dueDate || '',
            dueDate: dueDate || '',
            startDate: startDate || '',
            rentPeriod: 'yearly',
            status: 'submitted',
            chargeRangeStart: rangeStart,
            chargeRangeEnd: rangeEnd
          }
        })

        for (const item of itemsForSplit) {
          const vehicle =
            targetVehicles.find(v => (v._id || v.id || v.plate) === item.vehicleId) ||
            vehicles.find(v => (v._id || v.id || v.plate) === item.vehicleId)
          if (!vehicle) continue
          const docId = vehicle._id || vehicle.id || item.vehicleId
          await updateVehicle(docId, {
            _id: docId,
            dueDate: item.dueDate ? dayjs(item.dueDate).format('YYYY-MM-DD') : vehicle.dueDate,
            startDate: item.startDate ? dayjs(item.startDate).format('YYYY-MM-DD') : vehicle.startDate,
            rentPeriod: 'yearly',
            rentAmount: Number(vehicle.rentAmount || 0)
          })
        }

        const first = targetVehicles[0] || vehicles.find(v => (v._id || v.id || v.plate) === items[0]?.vehicleId)
        const evidenceUrls = await uploadEvidence(first?.plate || 'merged')
        const mergeRangeStart = itemsForSplit.find((it: any) => it.chargeRange?.[0])?.chargeRange?.[0]
        const mergeRangeEnd = itemsForSplit.find((it: any) => it.chargeRange?.[1])?.chargeRange?.[1]
        await simpleDirectCloudApi.addReceipt({
          vehicleId: first?._id || first?.id || first?.plate,
          plate: first?.plate || '合并收款',
          model: first?.type,
          company: first?.company,
          customerName: first?.company,
          amount: total,
          actualAmount: totalActual ? Number(totalActual) : undefined,
          dueDate: first?.dueDate,
          startDate: first?.startDate,
          rentPeriod: 'yearly',
          status: 'submitted',
          createdBy: makerName,
          makerId: makerIdValue,
          makerSealUrl,
          notes: notes || '',
          evidenceUrls,
          items: allocatedItems,
          mergeByCompany: true,
          chargeRangeStart: mergeRangeStart ? dayjs(mergeRangeStart).format('YYYY-MM-DD') : undefined,
          chargeRangeEnd: mergeRangeEnd ? dayjs(mergeRangeEnd).format('YYYY-MM-DD') : undefined
        })
      } else {
        for (const item of items) {
          const vehicle =
            selectedVehicles.find(v => (v._id || v.id || v.plate) === item.vehicleId) ||
            vehicles.find(v => (v._id || v.id || v.plate) === item.vehicleId)
          if (!vehicle) continue
          const docId = vehicle._id || vehicle.id || item.vehicleId
          const payAmount = Number(item.amount || vehicle.rentAmount || 0)
          const itemActual = Number(item.actualAmount || 0)
          const rangeStart = item.chargeRange?.[0] ? dayjs(item.chargeRange[0]).format('YYYY-MM-DD') : undefined
          const rangeEnd = item.chargeRange?.[1] ? dayjs(item.chargeRange[1]).format('YYYY-MM-DD') : undefined
          const allocatedItems = allocatePaymentByPeriod(
            vehicle,
            payAmount,
            undefined
          )
          const allocatedWithRange = allocatedItems.map((it: any) => ({
            ...it,
            chargeRangeStart: rangeStart,
            chargeRangeEnd: rangeEnd
          }))

          await updateVehicle(docId, {
            _id: docId,
            dueDate: item.dueDate ? dayjs(item.dueDate).format('YYYY-MM-DD') : vehicle.dueDate,
            startDate: item.startDate ? dayjs(item.startDate).format('YYYY-MM-DD') : vehicle.startDate,
            rentPeriod: 'yearly',
            rentAmount: Number(item.amount || vehicle.rentAmount || 0)
          })

          const evidenceUrls = await uploadEvidence(vehicle.plate || docId)

          await simpleDirectCloudApi.addReceipt({
            vehicleId: docId,
            plate: vehicle.plate,
            model: vehicle.type,
            company: vehicle.company,
            customerName: vehicle.company,
            amount: payAmount,
            actualAmount: itemActual ? Number(itemActual) : undefined,
            dueDate: item.dueDate ? dayjs(item.dueDate).format('YYYY-MM-DD') : vehicle.dueDate,
            startDate: item.startDate ? dayjs(item.startDate).format('YYYY-MM-DD') : vehicle.startDate,
            rentPeriod: 'yearly',
            status: 'submitted',
            createdBy: makerName,
            makerId: makerIdValue,
            makerSealUrl,
            notes: notes || '',
            evidenceUrls,
            items: allocatedWithRange,
            chargeRangeStart: rangeStart,
            chargeRangeEnd: rangeEnd
          })
        }
      }

      message.success('提交成功')
      setPreviewVisible(false)
      setRentModalVisible(false)
      setFileList([])
      setPendingValues(null)
      setSelectedVehicles([])
      loadData()} catch (err) {
      console.error(err)
      message.error('提交失败,请重试')
    }
  }

  const getExpiringVehicles = (limitDays?: number) => {
    const now = dayjs()
    const pendingIds = pendingReceipts.map(p => p.vehicleId || p.plate)
    return vehicles
      .filter(v => v.status === 'rented')
      .filter(v => getVehicleDueDate(v))
      .filter(v => isLeaseStarted(v, now))
      .filter(v => !pendingIds.includes(v.id) && !pendingIds.includes(v._id))
      .filter(v => {
        if (limitDays === undefined) return true
        const diff = dayjs(getVehicleDueDate(v)).diff(now, 'day')
        return diff <= limitDays && diff >= 0
      })
      .sort((a, b) => dayjs(getVehicleDueDate(a)).diff(dayjs(getVehicleDueDate(b))))
  }

  const getDueDateStatus = (dueDate?: string) => {
    if (!dueDate) return { color: 'default', text: '未设置' }
    const diff = dayjs(dueDate).diff(dayjs(), 'day')
    if (diff <= 0) return { color: 'error', text: '已逾期' }
    if (diff <= 3) return { color: 'error', text: `${diff} 天` }
    if (diff <= 7) return { color: 'warning', text: `${diff} 天` }
    return { color: 'success', text: dayjs(dueDate).format('YYYY-MM-DD') }
  }

  const formatDueTagText = (text: string) => {
    if (!text) return text
    if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return `下一个收款日期： ${text}`
    if (text.includes('天')) return `剩余 ${text}`
    return text
  }
  const expiringVehicles = getExpiringVehicles(7)
  const expiringVehiclesAll = getExpiringVehicles()
  const expiringTop = expiringVehiclesAll.slice(0, 3)
  const expiringDisplayList = useMemo(() => {
    if (!showAllExpiring) return expiringTop
    const start = (expiringPage - 1) * expiringPageSize
    return expiringVehiclesAll.slice(start, start + expiringPageSize)
  }, [showAllExpiring, expiringTop, expiringVehiclesAll, expiringPage, expiringPageSize])
  const activeExpiringVehicle = useMemo(
    () => expiringVehiclesAll.find(v => normalizeId(v._id || v.id || v.plate) === activeExpiringVehicleId),
    [expiringVehiclesAll, activeExpiringVehicleId]
  )
  const expiringDepositSum = expiringVehicles.reduce((sum, v) => sum + Number(v.depositAmount || 0), 0)
  const expiringReminderSum = expiringVehicles.reduce((sum, v) => sum + Number(v.reminderAmount ?? v.rentAmount ?? 0), 0)

  if (loading) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" />
      </div>
    )
  }
  return (
    <div style={{ padding: 24, background: '#f7f8fb', minHeight: '100vh' }}>
      <Card style={{ marginBottom: 24 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space size="large">
              <Avatar size={64} icon={<UserOutlined />} style={{ backgroundColor: '#cfa972' }} />
              <div>
                <Title level={4} style={{ margin: 0 }}>{user?.nickname || user?.nickName || user?.username || '租车管理员'}</Title>
                <Text type="secondary">{user?.info || '租车管理员'}</Text>
              </div>
            </Space>
          </Col>
          <Col>
            <Space>
              {canViewBoss && (
                <Button onClick={() => navigate('/boss')}>老板页面</Button>
              )}
              <Button icon={<LogoutOutlined />} onClick={handleLogout}>
              退出登录
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic title="总车辆数" value={statistics?.total || 0} prefix={<CarOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card hoverable onClick={() => setExpiringModalVisible(true)}>
            <Statistic title="即将到期车辆" value={statistics?.expiring || 0} valueStyle={{ color: '#d89614' }} prefix={<ClockCircleOutlined />} />
            <div style={{ marginTop: 4, color: '#999' }}>租金合计 ¥{expiringReminderSum.toFixed(2)}</div>
            <div style={{ marginTop: 2, color: '#999' }}>押金合计 ¥{expiringDepositSum.toFixed(2)}</div>
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic title="待收款收据" value={pendingReceipts.length} valueStyle={{ color: '#cfa972' }} prefix={<DollarOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic title="已逾期收据" value={statistics?.expired || 0} valueStyle={{ color: '#ff4d4f' }} prefix={<ExclamationCircleOutlined />} />
          </Card>
        </Col>
      </Row>


      <Card
        style={{
          marginBottom: 24,
          borderColor: expiringVehiclesAll.length ? 'var(--warning)' : 'var(--line)',
          background: expiringVehiclesAll.length ? 'rgba(192, 122, 31, 0.06)' : undefined
        }}
      >
        <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
          <Col>
            <Space>
              <ClockCircleOutlined style={{ color: 'var(--warning)' }} />
              <Text strong>到期提醒</Text>
              <Tag color={expiringVehiclesAll.length ? 'orange' : 'default'}>
                {expiringVehiclesAll.length} 台
              </Tag>
              {expiringVehiclesAll.length ? (
                <Text type="secondary">请优先处理即将到期的车辆</Text>
              ) : (
                <Text type="secondary">当前没有需要提醒的车辆</Text>
              )}
            </Space>
          </Col>
          <Col>
            <Button
              type="primary"
              onClick={() => {
                setShowAllExpiring(prev => {
                  const next = !prev
                  if (next) {
                    setExpiringPage(1)
                    setActiveExpiringVehicleId('')
                  }
                  return next
                })
              }}
              disabled={!expiringVehiclesAll.length}
            >
              {showAllExpiring ? '收起' : '查看全部'}
            </Button>
          </Col>
        </Row>
        {expiringVehiclesAll.length > 0 && (
          <>
            <List
              itemLayout="vertical"
              dataSource={expiringDisplayList}
              renderItem={(vehicle) => {
                const dueDate = getVehicleDueDate(vehicle)
                const vehicleId = normalizeId(vehicle._id || vehicle.id || vehicle.plate)
                const isActive = vehicleId === activeExpiringVehicleId
                return (
                  <List.Item
                    key={vehicleId}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setActiveExpiringVehicleId(vehicleId)}
                  >
                    <List.Item.Meta
                      avatar={<Avatar style={{ backgroundColor: '#cfa972' }}>{vehicle.plate}</Avatar>}
                      title={
                        <Space>
                          <Text strong>{vehicle.plate}</Text>
                          <Tag color="orange">下一个收款日期： {dueDate ? dayjs(dueDate).format('YYYY-MM-DD') : '-'}</Tag>
                          <Tag color="gold">租金 ¥{Number(vehicle.reminderAmount ?? vehicle.rentAmount ?? 0).toFixed(2)}</Tag>
                          {isActive && <Tag color="blue">已展开</Tag>}
                        </Space>
                      }
                      description={
                        <div>
                          <Text type="secondary">公司：</Text>
                          <Text>{vehicle.company || '-'}</Text>
                          <Text type="secondary" style={{ marginLeft: 12 }}>联系人：</Text>
                          <Text>{vehicle.contactPerson || '-'}</Text>
                        </div>
                      }
                    />
                  </List.Item>
                )
              }}
            />
            {showAllExpiring && expiringVehiclesAll.length > expiringPageSize && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                <Pagination
                  current={expiringPage}
                  pageSize={expiringPageSize}
                  total={expiringVehiclesAll.length}
                  showSizeChanger
                  pageSizeOptions={['8', '12', '20']}
                  showTotal={(total) => `共 ${total} 台`}
                  onChange={(page, size) => {
                    setExpiringPage(page)
                    if (size && size !== expiringPageSize) {
                      setExpiringPageSize(size)
                      setExpiringPage(1)
                    }
                  }}
                />
              </div>
            )}
          </>
        )}
        {activeExpiringVehicle && (
          <Card size="small" style={{ marginTop: 12, borderColor: 'var(--warning)' }}>
            <Row gutter={16}>
              <Col span={8}><Text type="secondary">车牌</Text> <Text strong>{activeExpiringVehicle.plate || '-'}</Text></Col>
              <Col span={8}><Text type="secondary">下一个收款日期</Text> <Text>{(() => {
                const due = getVehicleDueDate(activeExpiringVehicle)
                return due ? dayjs(due).format('YYYY-MM-DD') : '-'
              })()}</Text></Col>
              <Col span={8}><Text type="secondary">待收</Text> <Text strong style={{ color: '#cf1322' }}>￥{getOutstanding(activeExpiringVehicle).toFixed(2)}</Text></Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 8 }}>
              <Col span={8}><Text type="secondary">公司</Text> <Text>{activeExpiringVehicle.company || '-'}</Text></Col>
              <Col span={8}><Text type="secondary">联系人</Text> <Text>{activeExpiringVehicle.contactPerson || '-'}</Text></Col>
              <Col span={8}><Text type="secondary">电话</Text> <Text>{activeExpiringVehicle.phone || '-'}</Text></Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 8 }}>
              <Col span={8}><Text type="secondary">提醒金额</Text> <Text>￥{Number(activeExpiringVehicle.reminderAmount ?? activeExpiringVehicle.rentAmount ?? 0).toFixed(2)}</Text></Col>
              <Col span={8}><Text type="secondary">提醒周期</Text> <Text>{getPeriodLabel(activeExpiringVehicle.reminderPeriod || activeExpiringVehicle.rentPeriod, activeExpiringVehicle.reminderPeriodMonths)}</Text></Col>
              <Col span={8}><Text type="secondary">收款截止日期</Text> <Text>{(() => {
                const latestChargeRangeEnd =
                  latestChargeRangeEndByVehicleId.get(normalizeId(activeExpiringVehicle._id || activeExpiringVehicle.id || activeExpiringVehicle.plate)) ||
                  latestChargeRangeEndByPlate.get(String(activeExpiringVehicle.plate || '').trim()) ||
                  String(activeExpiringVehicle.startDate || '')
                return latestChargeRangeEnd && dayjs(latestChargeRangeEnd).isValid()
                  ? dayjs(latestChargeRangeEnd).format('YYYY-MM-DD')
                  : '-'
              })()}</Text></Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 8 }}>
              <Col span={24}><Text type="secondary">地址</Text> <Text>{activeExpiringVehicle.address || '-'}</Text></Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 8 }}>
              <Col span={24}><Text type="secondary">备注/配置</Text> <Text>{activeExpiringVehicle.configuration || activeExpiringVehicle.notes || '-'}</Text></Col>
            </Row>
            <Space style={{ marginTop: 12 }}>
              <Button type="primary" onClick={() => handleOpenRentForm(activeExpiringVehicle)} disabled={!canManageVehicles}>去收款</Button>
              <Button onClick={() => handleOpenRentForm(activeExpiringVehicle)} disabled={!canManageVehicles}>修改/续租</Button>
              <Button onClick={() => jumpToVehicleDetails(activeExpiringVehicle)}>定位到在租列表</Button>
            </Space>
          </Card>
        )}
      </Card>

      <Row gutter={16} style={{ marginBottom: 24 }} justify="space-between" align="middle">
        <Col>
          <Button type="primary" icon={<EditOutlined />} onClick={() => handleOpenRentForm()} size="large" disabled={!canManageVehicles}>
          新增车辆
          </Button>
        </Col>
        <Col>
          <Space>
            <Button onClick={openLeaseModal} disabled={!canManageVehicles}>
              退租/再出租单
            </Button>
            <Button onClick={() => { setRentAdjustFileList([]); setRentAdjustModalVisible(true) }} disabled={!canManageVehicles}>
              更新租金申请
            </Button>
          </Space>
        </Col>
        <Col>
          <Input.Search
            placeholder="车牌号/车型/公司/联系人/电话..."
            allowClear
            enterButton={<SearchOutlined />}
            size="large"
            style={{ width: '100%', maxWidth: 360 }}
            onSearch={handleSearch}
            onChange={(e) => {
              if (!e.target.value) handleSearch('')
            }}
          />
        </Col>
      </Row>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col>
          <Space>
            <Text type="secondary">片区筛选：</Text>
            {(['all', 'A', 'B', 'C', 'D'] as const).map(area => (
              <Button
                key={area}
                size="small"
                type={areaFilter === area ? 'primary' : 'default'}
                onClick={() => {
                  setAreaFilter(area)
                  setCurrentPage(1)
                }}
              >
                {area === 'all' ? '全部' : `${area}片区`}
              </Button>
            ))}
          </Space>
        </Col>
      </Row>

      <Card ref={rentDetailsRef}>
        <Tabs
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as any)}
          items={[
            {
              key: 'rent',
              label: `在租车辆 (${filteredRentedVehicles.length})`,
              children: (
                <>
                  <List
                    itemLayout="vertical"
                    dataSource={paginatedRentedVehicles}
                    renderItem={(vehicle) => {
                      const leaseStarted = isLeaseStarted(vehicle, dayjs())
                      const dueDate = getVehicleDueDate(vehicle)
                      const latestChargeRangeEnd =
                        latestChargeRangeEndByVehicleId.get(normalizeId(vehicle._id || vehicle.id || vehicle.plate)) ||
                        latestChargeRangeEndByPlate.get(String(vehicle.plate || '').trim()) ||
                        ''
                      const receiptDeadlineDisplay =
                        latestChargeRangeEnd ||
                        String(vehicle.startDate || '')
                      const dueStatus = leaseStarted
                        ? getDueDateStatus(dueDate)
                        : { color: 'default', text: '未开始' }
                      const dueTagText = formatDueTagText(dueStatus.text)
                      const outstanding = getOutstanding(vehicle)
                      return (
                        <List.Item
                          key={vehicle._id || vehicle.id || vehicle.plate}
                          actions={[
                            <Button key="edit" type="link" icon={<EditOutlined />} onClick={() => handleOpenRentForm(vehicle)} disabled={!canManageVehicles}>
                              修改/续租
                            </Button>,
                            <Button key="collect" type="link" icon={<CheckCircleOutlined />} onClick={() => handleOpenRentForm(vehicle)} disabled={!canManageVehicles}>
                              收款
                            </Button>
                          ]}
                        >
                          <List.Item.Meta
                            avatar={<Avatar style={{ backgroundColor: '#cfa972' }}>{vehicle.plate}</Avatar>}
                            title={
                              <Space>
                                <Text strong>{vehicle.plate}</Text>
                                <Tag color={dueStatus.color}>{dueTagText}</Tag>
                                {vehicle.area && <Tag color="geekblue">{normalizeArea(vehicle.area)}片区</Tag>}
                                <Tag color={outstanding > 0 ? 'red' : 'green'}>
                                待收 ￥{outstanding.toFixed(2)}
                                </Tag>
                              </Space>
                            }
                            description={
                              <div>
                                <Row gutter={16}>
                                  <Col span={12}><Text type="secondary">车型</Text> <Text>{vehicle.type}</Text></Col>
                                  <Col span={12}><Text type="secondary">公司</Text> <Text>{vehicle.company}</Text></Col>
                                </Row>
                                <Row gutter={16}>
                                  <Col span={12}><Text type="secondary">联系人</Text> <Text>{vehicle.contactPerson || '-'} {(vehicle as any).contactPerson2 ? `/ ${(vehicle as any).contactPerson2}` : ''}</Text></Col>
                                  <Col span={12}><Text type="secondary">电话</Text> <Text>{vehicle.phone} {(vehicle as any).phone2 ? `/ ${(vehicle as any).phone2}` : ''}</Text></Col>
                                </Row>
                                <Row gutter={16}>
                                  <Col span={12}><Text type="secondary">租金</Text> <Text strong style={{ color: '#52c41a' }}>￥{vehicle.rentAmount || 0}</Text></Col>
                                  <Col span={12}><Text type="secondary">地址</Text> <Text>{vehicle.address || '-'}</Text></Col>
                                </Row>
                                <Row gutter={16}>
                                  <Col span={12}><Text type="secondary">提醒金额</Text> <Text>￥{vehicle.reminderAmount ?? vehicle.rentAmount ?? 0}</Text></Col>
                                  <Col span={12}><Text type="secondary">提醒周期</Text> <Text>{getPeriodLabel(vehicle.reminderPeriod || vehicle.rentPeriod, vehicle.reminderPeriodMonths)}</Text></Col>
                                </Row>
                                <Row gutter={16}>
                                  <Col span={12}><Text type="secondary">押金</Text> <Text>{vehicle.depositAmount ?? '-'}</Text></Col>
                                  <Col span={12}><Text type="secondary">收款截止日期</Text> <Text>{receiptDeadlineDisplay && dayjs(receiptDeadlineDisplay).isValid() ? dayjs(receiptDeadlineDisplay).format('YYYY-MM-DD') : '-'}</Text></Col>
                                </Row>
                                <Row gutter={16}>
                                  <Col span={24}><Text type="secondary">备注</Text> <Text>{vehicle.notes || '-'}</Text></Col>
                                </Row>
                              </div>
                            }
                          />
                        </List.Item>
                      )
                    }}
                  />
                  {filteredRentedVehicles.length > 0 && (
                    <div style={{ textAlign: 'center', marginTop: 16 }}>
                      <Pagination
                        current={currentPage}
                        pageSize={pageSize}
                        total={filteredRentedVehicles.length}
                        showSizeChanger
                        showQuickJumper
                        pageSizeOptions={['10', '20', '50']}
                        onChange={(page, size) => {
                          setCurrentPage(page)
                          if (size !== pageSize) {
                            setPageSize(size)
                            setCurrentPage(1)
                          }
                        }}
                        onShowSizeChange={(_, size) => {
                          setPageSize(size)
                          setCurrentPage(1)
                        }}
                        showTotal={(total, range) => `第 ${range[0]}-${range[1]} 条 / 共 ${total} 条`}
                      />
                    </div>
                  )}
                </>
              )
            },
            {
              key: 'pending',
              label: (
                <Badge count={signedReceiptDocs.length + signedLeaseForms.length} size="small">
                  <span>已签单据</span>
                </Badge>
              ),
              children: (
                <>
                  <List
                    itemLayout="vertical"
                    dataSource={signedReceiptDocs}
                    locale={{ emptyText: '暂无已签收款收据' }}
                    renderItem={(receipt: any) => {
                      const vehicle = vehicles.find(v => v.id === receipt.vehicleId || v._id === receipt.vehicleId || v.plate === receipt.plate)
                      const plate = receipt.plate || vehicle?.plate || '未知车牌'
                      return (
                        <List.Item
                          key={receipt._id || receipt.id || plate}
                          actions={[
                            <Button key="print" type="primary" onClick={() => handlePrintReceipt(receipt)}>
                              打印收据
                            </Button>,
                            receipt.cashierSignatureUrl ? (
                              <Button key="sig" onClick={() => openCloudFile(receipt.cashierSignatureUrl)}>
                                查看出纳签名
                              </Button>
                            ) : null
                          ].filter((action) => Boolean(action))}
                        >
                          <List.Item.Meta
                            avatar={<Avatar style={{ backgroundColor: '#52c41a' }}>{plate}</Avatar>}
                            title={
                              <Space>
                                <Text strong>{plate}</Text>
                                <Tag color="green">已签收据</Tag>
                              </Space>
                            }
                            description={(
                              <div>
                                <Row gutter={16}>
                                  <Col span={12}><Text type="secondary">公司</Text> <Text>{receipt.company || vehicle?.company || '-'}</Text></Col>
                                  <Col span={12}><Text type="secondary">金额</Text> <Text strong>￥{Number(receipt.amount || 0).toFixed(2)}</Text></Col>
                                </Row>
                                <Row gutter={16}>
                                  <Col span={12}><Text type="secondary">制单人</Text> <Text>{receipt.createdBy || '-'}</Text></Col>
                                  <Col span={12}><Text type="secondary">出纳</Text> <Text>{receipt.cashierName || '-'}</Text></Col>
                                </Row>
                                <Row gutter={16}>
                                  <Col span={12}><Text type="secondary">签名时间</Text> <Text>{receipt.verifiedAt ? dayjs(receipt.verifiedAt).format('YYYY-MM-DD HH:mm') : '-'}</Text></Col>
                                  <Col span={12}><Text type="secondary">状态</Text> <Tag color="green">{receipt.status || '-'}</Tag></Col>
                                </Row>
                              </div>
                            )}
                          />
                        </List.Item>
                      )
                    }}
                  />

                  <Divider orientation="left">已签退租/再出租单（可打印封存）</Divider>
                  <List
                    itemLayout="vertical"
                    dataSource={signedLeaseForms}
                    locale={{ emptyText: '暂无已签退租/再出租单' }}
                    renderItem={(doc: any) => (
                      <List.Item
                        key={doc._id || doc.id || doc.plate}
                        actions={[
                          <Button key="print" type="primary" onClick={() => handlePrintLeaseForm(doc)}>
                            打印单据
                          </Button>,
                          doc.cashierSignatureUrl ? (
                            <Button key="sig" onClick={() => openCloudFile(doc.cashierSignatureUrl)}>
                              查看出纳签名
                            </Button>
                          ) : null
                        ].filter((action) => Boolean(action))}
                      >
                        <List.Item.Meta
                          avatar={<Avatar style={{ backgroundColor: '#cfa972' }}>{doc.plate || '-'}</Avatar>}
                          title={(
                            <Space>
                              <Text strong>{doc.plate || '-'}</Text>
                              <Tag color={doc.type === 'return' ? 'volcano' : 'blue'}>
                                {doc.type === 'return' ? '退租单' : '出租单'}
                              </Tag>
                              <Tag color="green">已签名</Tag>
                            </Space>
                          )}
                          description={(
                            <div>
                              <Row gutter={16}>
                                <Col span={12}><Text type="secondary">公司</Text> <Text>{doc.company || '-'}</Text></Col>
                                <Col span={12}><Text type="secondary">{doc.type === 'relet' ? '出租单位' : '退租单位'}</Text> <Text>{doc.unitName || '-'}</Text></Col>
                              </Row>
                              <Row gutter={16}>
                                <Col span={12}><Text type="secondary">制单人</Text> <Text>{doc.createdBy || '-'}</Text></Col>
                                <Col span={12}><Text type="secondary">出纳</Text> <Text>{doc.cashierName || '-'}</Text></Col>
                              </Row>
                              <Row gutter={16}>
                                <Col span={12}><Text type="secondary">签名时间</Text> <Text>{doc.signedAt ? dayjs(doc.signedAt).format('YYYY-MM-DD HH:mm') : '-'}</Text></Col>
                                <Col span={12}><Text type="secondary">备注</Text> <Text>{doc.notes || '-'}</Text></Col>
                              </Row>
                            </div>
                          )}
                        />
                      </List.Item>
                    )}
                  />
                </>
              )
            },
            {
              key: 'rentAdjust',
              label: (
                <Badge count={rentAdjustRequests.length} size="small">
                  <span>租金申请记录</span>
                </Badge>
              ),
              children: (
                <List
                  itemLayout="vertical"
                  dataSource={[...rentAdjustRequests].sort((a: any, b: any) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf())}
                  locale={{ emptyText: '暂无记录' }}
                  renderItem={(item: any) => (
                    <List.Item>
                      <List.Item.Meta
                        avatar={<Avatar style={{ backgroundColor: '#cfa972' }}>{item.plate || '-'}</Avatar>}
                        title={(
                          <Space>
                            <Text strong>{item.plate || '-'}</Text>
                            <Tag color={item.status === 'approved' ? 'green' : item.status === 'filed' ? 'blue' : 'orange'}>
                              {item.status === 'approved' ? '已处理' : item.status === 'filed' ? '已归档' : '待处理'}
                            </Tag>
                          </Space>
                        )}
                        description={(
                          <div>
                            <Row gutter={16}>
                              <Col span={12}><Text type="secondary">公司</Text> <Text>{item.company || '-'}</Text></Col>
                              <Col span={12}><Text type="secondary">当前租金</Text> <Text>￥{Number(item.currentRent || 0).toFixed(2)}</Text></Col>
                            </Row>
                            <Row gutter={16}>
                              <Col span={12}><Text type="secondary">制单人</Text> <Text>{item.createdBy || '-'}</Text></Col>
                              <Col span={12}><Text type="secondary">提交时间</Text> <Text>{item.createdAt ? dayjs(item.createdAt).format('YYYY-MM-DD HH:mm') : '-'}</Text></Col>
                            </Row>
                            <Row gutter={16}>
                              <Col span={24}><Text type="secondary">说明</Text> <Text>{item.notes || '-'}</Text></Col>
                            </Row>
                            <Row gutter={16}>
                              <Col span={24}>
                                <Text type="secondary">附件</Text>{' '}
                                {Array.isArray(item.attachmentUrls) && item.attachmentUrls.length ? (
                                  <Space wrap>
                                    {item.attachmentUrls.map((url: string, idx: number) => (
                                      <Button key={`${url}-${idx}`} size="small" onClick={() => openCloudFile(url)}>
                                        查看附件{idx + 1}
                                      </Button>
                                    ))}
                                  </Space>
                                ) : (
                                  <Text>-</Text>
                                )}
                              </Col>
                            </Row>
                          </div>
                        )}
                      />
                    </List.Item>
                  )}
                />
              )
            },
          ]}
        />
      </Card>

      <ExpiringVehiclesModal
        visible={expiringModalVisible}
        vehicles={expiringVehiclesAll}
        onClose={() => setExpiringModalVisible(false)}
        getDueDateStatus={getDueDateStatus}
        onEdit={(vehicle) => handleOpenRentForm(vehicle)}
        canEdit={canManageVehicles}
      />

      <RentModal
        visible={rentModalVisible}
        onOk={handlePreviewRentForm}
        onClose={() => setRentModalVisible(false)}
        form={form}
        mergeByCompany={mergeByCompany}
        onMergeByCompanyChange={setMergeByCompany}
        onVehicleSelectionChange={handleVehicleSelectionChange}
        selectedVehicles={selectedVehicles}
        vehicles={vehicles}
        makers={makers}
        getOutstanding={getOutstanding}
        fileList={fileList}
        onFileListChange={setFileList}
      />

      <Modal
        title="退租/再出租单"
        open={leaseModalVisible}
        onOk={handleSubmitLeaseForm}
        onCancel={() => {
          setLeaseModalVisible(false)
          leaseForm.resetFields()
          const match = makers.find(m =>
            [m._id, m.id, m.username, m.nickName].some(val => String(val || '') === String(user?.id || user?.nickName || ''))
          )
          const defaultMakerId = match ? String(match._id || match.id || match.username || match.nickName) : undefined
          leaseForm.setFieldsValue({ type: 'return', makerId: defaultMakerId })
        }}
        width={720}
      >
        <div style={{ fontFamily: '"SimSun","STSong","Noto Serif SC",serif', color: '#222' }}>
          <Form form={leaseForm} layout="vertical">
            <div style={{ textAlign: 'center', fontSize: 26, fontWeight: 700, letterSpacing: 6 }}>
              {leaseType === 'relet' ? '出租单' : '退租单'}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
              <Form.Item
                name="type"
                rules={[{ required: true, message: '请选择类型' }]}
                style={{ marginBottom: 0, width: '45%' }}
              >
                <Select placeholder="选择类型">
                  <Option value="return">退租单</Option>
                  <Option value="relet">出租单</Option>
                </Select>
              </Form.Item>
              <Form.Item
                name="vehicleId"
                rules={[{ required: true, message: '请选择车辆' }]}
                style={{ marginBottom: 0, width: '45%' }}
              >
                <Select
                  placeholder="选择车辆"
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {vehicles.map(v => {
                    const id = String(v._id || v.id || v.plate)
                    return (
                      <Option key={id} value={id} disabled={v.returnSigned}>
                        {v.plate} - {v.company}
                      </Option>
                    )
                  })}
                </Select>
              </Form.Item>
            </div>
            <Form.Item shouldUpdate={(prev, cur) => prev.type !== cur.type} noStyle>
              {() => {
                const label = leaseType === 'relet' ? '出租单位' : '退租单位'
                const isReturnType = !leaseType || leaseType === 'return'
                return (
                  <div style={{ marginTop: 8 }}>
                    <div>{label}：</div>
                    <Form.Item
                      name="unitName"
                      rules={[{ required: true, message: `请输入${label}` }]}
                      style={{ marginBottom: 0 }}
                    >
                      <Input
                        placeholder={`请输入${label}`}
                        disabled={isReturnType}
                      />
                    </Form.Item>
                  </div>
                )
              }}
            </Form.Item>

            <div style={{ border: '1px solid #333', marginTop: 12 }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '80px 1fr 150px 60px 60px 90px 110px',
                fontWeight: 700,
                borderBottom: '1px solid #333'
              }}>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>货号</div>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>品名及规格</div>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>
                  {leaseType === 'relet' ? '出租单位' : '退租单位'}
                </div>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>单位</div>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>数量</div>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>单价</div>
                <div style={{ padding: '6px 8px' }}>金额</div>
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '80px 1fr 150px 60px 60px 90px 110px'
              }}>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }} />
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>
                  {(() => {
                    const vehicle = vehicles.find(v => String(v._id || v.id || v.plate) === String(leaseVehicleId))
                    const plate = vehicle?.plate || '-'
                    const model = vehicle?.type ? ` - ${vehicle.type}` : ''
                    return `车辆租赁（${plate}）${model}`
                  })()}
                </div>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>
                  {leaseUnitName || '-'}
                </div>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>台</div>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>1</div>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333', textAlign: 'right' }}>-</div>
                <div style={{ padding: '6px 8px', textAlign: 'right' }}>-</div>
              </div>
            </div>
            <div style={{ marginTop: 8 }}>
              <div>配置：</div>
              <div>
                {(() => {
                  const vehicle = vehicles.find(v => String(v._id || v.id || v.plate) === String(leaseVehicleId))
                  return vehicle?.configuration || vehicle?.notes || '-'
                })()}
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 8 }}>
              <div>
                <div style={{ marginBottom: 4 }}>制单人：</div>
                <Form.Item name="makerId" style={{ marginBottom: 8, width: 220 }} rules={[{ required: true, message: '请选择制单人' }]}>
                  <Select placeholder="选择制单人">
                    {makers.map((m: any) => {
                      const id = String(m._id || m.id || m.username || m.nickName)
                      return (
                        <Option key={id} value={id}>
                          {m.nickName || m.username || id}
                        </Option>
                      )
                    })}
                  </Select>
                </Form.Item>
                {leaseMakerSealPreviewUrl ? (
                  <img
                    src={leaseMakerSealPreviewUrl}
                    alt="maker seal"
                    style={{ maxWidth: 220, maxHeight: 120, border: '1px solid #ddd', borderRadius: 4 }}
                  />
                ) : (
                  <div style={{ color: '#999' }}>-</div>
                )}
              </div>
              <div>
                <div style={{ marginBottom: 4 }}>出纳：</div>
                <div style={{ minHeight: 32, display: 'flex', alignItems: 'center' }}>-</div>
                <div style={{ marginTop: 8, color: '#999' }}>-</div>
              </div>
            </div>
            <div style={{ marginTop: 8 }}>
              <div>备注：</div>
              <Form.Item name="notes" style={{ marginBottom: 0 }}>
                <Input.TextArea rows={2} placeholder="备注(可选)" />
              </Form.Item>
            </div>
          </Form>
        </div>
      </Modal>

      <Modal
        title="更新租金申请"
        open={rentAdjustModalVisible}
        onOk={handleSubmitRentAdjustRequest}
        onCancel={() => {
          setRentAdjustModalVisible(false)
          rentAdjustForm.resetFields()
          setRentAdjustFileList([])
        }}
        width={720}
      >
        <div style={{ fontFamily: '"SimSun","STSong","Noto Serif SC",serif', color: '#222' }}>
          <Form form={rentAdjustForm} layout="vertical">
            <div style={{ textAlign: 'center', fontSize: 26, fontWeight: 700, letterSpacing: 6 }}>
              更新租金申请单
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
              <div>申请人：{resolveMaker(rentAdjustMakerId)?.nickName || user?.nickName || '-'}</div>
              <div>日期：{dayjs().format('YYYY-MM-DD')}</div>
            </div>
            <Form.Item name="vehicleId" label="选择车辆" rules={[{ required: true, message: '请选择车辆' }]}>
              <Select
                placeholder="选择车辆"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                }
              >
                {vehicles.map(v => {
                  const id = String(v._id || v.id || v.plate)
                  return (
                    <Option key={id} value={id}>
                      {v.plate} - {v.company}
                    </Option>
                  )
                })}
              </Select>
            </Form.Item>
            <div style={{ border: '1px solid #333', marginTop: 12 }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '80px 1fr 120px 120px',
                fontWeight: 700,
                borderBottom: '1px solid #333'
              }}>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>货号</div>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>品名及规格</div>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>当前租金</div>
                <div style={{ padding: '6px 8px' }}>调整后租金</div>
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '80px 1fr 120px 120px'
              }}>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }} />
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>
                  {(() => {
                    const vehicle = vehicles.find(v => String(v._id || v.id || v.plate) === String(rentAdjustVehicleId))
                    const plate = vehicle?.plate || '-'
                    const spec = [vehicle?.type, vehicle?.configuration || vehicle?.notes].filter(Boolean).join(' / ')
                    return `车辆租赁（${plate}）${spec ? ` - ${spec}` : ''}`
                  })()}
                </div>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #333' }}>
                  ￥{(() => {
                    const vehicle = vehicles.find(v => String(v._id || v.id || v.plate) === String(rentAdjustVehicleId))
                    return Number(vehicle?.rentAmount || 0).toFixed(2)
                  })()}
                </div>
                <div style={{ padding: '6px 8px' }}>
                  （人工线下调整）
                </div>
              </div>
            </div>
            <div style={{ marginTop: 8 }}>
              <div>申请说明：</div>
              <Form.Item name="notes" style={{ marginBottom: 0 }}>
                <Input.TextArea rows={2} placeholder="说明(可选)" />
              </Form.Item>
            </div>
            <div style={{ marginTop: 8 }}>
              <div>上传租金申请单：</div>
              <Upload
                fileList={rentAdjustFileList}
                beforeUpload={() => false}
                onChange={({ fileList }) => setRentAdjustFileList(fileList)}
              >
                <Button>上传图片/PDF</Button>
              </Upload>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
              <div>制单人：</div>
              <Form.Item name="makerId" style={{ marginBottom: 0, width: 220 }} rules={[{ required: true, message: '请选择制单人' }]}>
                <Select placeholder="选择制单人">
                  {makers.map((m: any) => {
                    const id = String(m._id || m.id || m.username || m.nickName)
                    return (
                      <Option key={id} value={id}>
                        {m.nickName || m.username || id}
                      </Option>
                    )
                  })}
                </Select>
              </Form.Item>
              <div>处理方式：</div>
              <div>上传归档</div>
            </div>
            {rentAdjustMakerSealPreviewUrl && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                <img
                  src={rentAdjustMakerSealPreviewUrl}
                  alt="maker seal"
                  style={{ maxWidth: 220, maxHeight: 120, border: '1px solid #ddd', borderRadius: 4 }}
                />
              </div>
            )}
          </Form>
        </div>
      </Modal>

      <PreviewModal
        visible={previewVisible}
        onClose={() => setPreviewVisible(false)}
        onConfirm={handleConfirmSubmit}
        previewItems={previewItems}
        fileList={fileList}
      />
    </div>
  )
}

export default VehicleManagerPage
